import * as bip39 from 'bip39';
import { initWallet } from './bitcoin-ops.js';

// Uses WebCrypto API to encrypt the BIP39 mnemonic with a user password
// and stores it safely in localStorage.

const STORE_KEY = 'elara_keystore';

async function deriveKeyFromName(password, salt) {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
        "raw",
        enc.encode(password),
        { name: "PBKDF2" },
        false,
        ["deriveBits", "deriveKey"]
    );
    return crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: salt,
            iterations: 100000,
            hash: "SHA-256"
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
}

export async function generate12WordMnemonic() {
    return bip39.generateMnemonic(128);
}

export async function encryptKeyStore(mnemonic, password) {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const key = await deriveKeyFromName(password, salt);

    const enc = new TextEncoder();
    const encryptedContent = await crypto.subtle.encrypt(
        {
            name: "AES-GCM",
            iv: iv
        },
        key,
        enc.encode(mnemonic)
    );

    const encryptedData = {
        salt: Array.from(salt),
        iv: Array.from(iv),
        content: Array.from(new Uint8Array(encryptedContent))
    };

    localStorage.setItem(STORE_KEY, JSON.stringify(encryptedData));
    return true;
}

export async function decryptKeyStore(password) {
    const stored = localStorage.getItem(STORE_KEY);
    if (!stored) return null;

    const data = JSON.parse(stored);
    const salt = new Uint8Array(data.salt);
    const iv = new Uint8Array(data.iv);
    const content = new Uint8Array(data.content);

    try {
        const key = await deriveKeyFromName(password, salt);
        const decryptedContent = await crypto.subtle.decrypt(
            {
                name: "AES-GCM",
                iv: iv
            },
            key,
            content
        );

        const dec = new TextDecoder();
        return dec.decode(decryptedContent);
    } catch (e) {
        throw new Error("Invalid password or corrupted keystore.");
    }
}

export async function loadWalletFromMnemonic(mnemonic) {
    return initWallet(mnemonic);
}
